import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import { CheckCircle, XCircle, Clock, FileText } from "@phosphor-icons/react";

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

export default function ReviewRequests() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/review-requests?status_filter=${tab}`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load review requests");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab]);

  const approve = async (rid) => {
    setBusyId(rid);
    try {
      await api.post(`/review-requests/${rid}/approve`);
      toast.success("Request approved — student can now see the detailed review");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setBusyId(null); }
  };

  const submitReject = async () => {
    if (!rejectReason.trim()) { toast.error("Reason is required"); return; }
    const rid = rejectingId;
    setBusyId(rid);
    try {
      await api.post(`/review-requests/${rid}/reject`, { rejection_reason: rejectReason.trim() });
      toast.success("Request rejected");
      setRejectingId(null);
      setRejectReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setBusyId(null); }
  };

  const counts = useMemo(() => rows.reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {}), [rows]);

  return (
    <div data-testid="review-requests-page">
      <PageHeader
        overline="Answer reviews"
        title="Review requests"
        subtitle="Students can request the detailed answer breakdown for their submitted attempts. Approve to reveal correct answers &amp; explanations."
      />

      <div className="flex gap-6 border-b border-zinc-200 mb-6" data-testid="review-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`py-3 -mb-px border-b-2 text-xs uppercase tracking-widest font-semibold ${tab === t.id ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
            data-testid={`review-tab-${t.id}`}
          >
            {t.label}{tab === t.id ? ` · ${rows.length}` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-10">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-zinc-500 py-16 text-center border border-dashed border-zinc-300" data-testid="review-empty">
          No {tab} review requests.
        </div>
      ) : (
        <div className="space-y-3" data-testid="review-list">
          {rows.map((r) => (
            <div key={r.request_id} className="border border-zinc-200 p-4" data-testid={`review-row-${r.request_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusBadge status={r.status} />
                    <span className="text-[10px] uppercase tracking-wider text-zinc-400">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <div className="font-display text-lg font-bold truncate">{r.student_name}</div>
                  <div className="text-xs text-zinc-600 mt-0.5">
                    <span className="font-mono">{r.exam_title || r.exam_id}</span>
                    <Link to={`/app/results/${r.attempt_id}`} className="ml-3 underline text-zinc-700 hover:text-zinc-900 inline-flex items-center gap-1" data-testid={`review-open-${r.request_id}`}>
                      <FileText size={11} /> Open attempt
                    </Link>
                  </div>
                  {r.reason && (
                    <div className="text-xs text-zinc-700 mt-2 border-l-2 border-zinc-300 pl-3 italic" data-testid={`review-reason-${r.request_id}`}>
                      &ldquo;{r.reason}&rdquo;
                    </div>
                  )}
                  {r.status === "rejected" && r.rejection_reason && (
                    <div className="text-xs text-red-800 mt-2 border-l-2 border-red-500 pl-3">
                      <span className="font-semibold">Rejected:</span> {r.rejection_reason}
                      <span className="text-zinc-500 ml-2">— {r.decided_by_name}</span>
                    </div>
                  )}
                  {r.status === "approved" && (
                    <div className="text-xs text-green-800 mt-2">
                      Approved by <span className="font-semibold">{r.decided_by_name}</span> on {new Date(r.decided_at).toLocaleString()}
                    </div>
                  )}
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => approve(r.request_id)}
                      disabled={busyId === r.request_id}
                      className="inline-flex items-center gap-1 bg-green-600 text-white px-3 py-2 text-xs font-semibold hover:bg-green-700 disabled:opacity-50"
                      data-testid={`review-approve-${r.request_id}`}
                    >
                      <CheckCircle size={12} weight="fill" /> Approve
                    </button>
                    <button
                      onClick={() => { setRejectingId(r.request_id); setRejectReason(""); }}
                      className="inline-flex items-center gap-1 border border-red-600 text-red-700 px-3 py-2 text-xs font-semibold hover:bg-red-50"
                      data-testid={`review-reject-${r.request_id}`}
                    >
                      <XCircle size={12} weight="fill" /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {rejectingId && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => setRejectingId(null)} data-testid="review-reject-modal">
          <div className="bg-white max-w-md w-full border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-zinc-200 bg-red-50">
              <div className="text-[10px] uppercase tracking-[0.25em] text-red-700 font-semibold">Reject review request</div>
              <div className="font-display text-lg font-bold text-red-900 mt-1">Reason required</div>
              <div className="text-xs text-red-800/80 mt-1">The reason you enter will be shown to the student.</div>
            </div>
            <div className="p-5">
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                placeholder="e.g. Answers are final for this examination cycle."
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:border-red-600 focus:outline-none"
                data-testid="review-reject-reason"
                maxLength={1000}
              />
              <div className="text-[10px] text-zinc-500 mt-1 text-right">{rejectReason.length}/1000</div>
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2">
              <button onClick={() => setRejectingId(null)} className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900" data-testid="review-reject-cancel">Cancel</button>
              <button
                onClick={submitReject}
                disabled={!rejectReason.trim() || busyId === rejectingId}
                className="px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
                data-testid="review-reject-confirm"
              >
                {busyId === rejectingId ? "Rejecting…" : "Reject request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { icon: Clock, label: "Pending", cls: "text-amber-800 bg-amber-100 border-amber-300" },
    approved: { icon: CheckCircle, label: "Approved", cls: "text-green-800 bg-green-100 border-green-300" },
    rejected: { icon: XCircle, label: "Rejected", cls: "text-red-800 bg-red-100 border-red-300" },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[9px] uppercase tracking-widest font-semibold ${s.cls}`}>
      <Icon size={10} weight="fill" /> {s.label}
    </span>
  );
}
